import { Modal } from "antd";
import * as Icons from "@ant-design/icons";
import * as React from "react";
import { isArray } from "lodash";
import { upload } from "@/api";
import http from "@/utils/request";

export const getRandomInt = (min: number, max: number) => {};

export const getTimeFormat = (
  time: string | Date | number,
  format = "yyyy-MM-dd HH:mm:ss"
): string => {
  const t = new Date(time);
  const year = t.getFullYear();
  const month = t.getMonth() + 1;
  const day = t.getDate();
  const hour = t.getHours();
  const minute = t.getMinutes();
  const second = t.getSeconds();

  return format
    .replace("yyyy", year.toString())
    .replace("MM", month.toString().padStart(2, "0"))
    .replace("dd", day.toString().padStart(2, "0"))
    .replace("HH", hour.toString().padStart(2, "0"))
    .replace("mm", minute.toString().padStart(2, "0"))
    .replace("ss", second.toString().padStart(2, "0"));
};

export const getDays = (time: string | Date | number): string => {
  const days = ["日", "一", "二", "三", "四", "五", "六"];
  return "周" + days[new Date(time).getDay()];
};

export const setIcons = (
  name: string,
  style?: React.CSSProperties
): React.ReactElement => {
  let element;
  try {
    element = Icons && (Icons as any)?.[name] && React.createElement((Icons as any)[name], {
      style,
    });
  } catch (error) {
    element = React.createElement("div", {});
  }
  return element;
};

export const getStorage = (key: string): string | Record<string, any> => {
  return JSON.parse(localStorage.getItem(key) || "null");
};

export const setStorage = (key: string, value: string | object): void => {
  localStorage.setItem(key, JSON.stringify(value));
};

export const removeStorage = (key: string): void => {
  localStorage.removeItem(key);
};

export const showModal = (data: {
  title: string;
  content: string | React.ReactNode;
  footer?: null;
  type?: null;
}): Promise<any> => {
  const { title, content, footer } = data;
  const promise = new Promise((res, rej) => {
    Modal.confirm({
      title: title,
      content: content,
      closable: true,
      footer: footer,
      cancelText: "取消",
      okText: "确定",
      onOk() {
        res("ok");
      },
      onCancel() {
        res("cancel");
      },
    });
  });
  return promise;
};

interface TreeNode {
  id: number;
  name: string;
  parentId?: number;
  children?: TreeNode[];
}

export const convertToTree = (
  flatArray: any[],
  labelReset: { id: string; name: string; parentId: string } = {
    id: "id",
    name: "name",
    parentId: "parentId",
  }
): any[] => {
  const { id, name, parentId } = labelReset;
  const map = new Map<number, TreeNode>();
  const tree: TreeNode[] = [];

  flatArray.forEach((node) => {
    map.set(node[id], { ...node, children: null });
  });

  flatArray.forEach((node) => {
    if (node[parentId] !== 0) {
      const parent = map.get(node[parentId] as number);
      if (parent) {
        parent.children = parent.children || [];
        parent.children?.push(map.get(node[id])!);
      } else {
        tree.push(map.get(node[id])!);
      }
    } else {
      tree.push(map.get(node[id])!);
    }
  });
  return tree;
};

export const resetForm = (form: Record<string, any>): Record<string, any> => {
  for (const key in form) {
    if (form.hasOwnProperty(key)) {
      if (key === "ypageNum") {
        form[key] = 1;
      } else if (key === "ypageSize") {
        form[key] = 10;
      } else if (typeof form[key] === "string") {
        form[key] = "";
      } else if (typeof form[key] === "number") {
        form[key] = undefined;
      } else if (Array.isArray(form[key])) {
        form[key] = [];
      } else if (typeof form[key] === "object" && form[key] !== null) {
        form[key] = {};
      }
    }
  }
  return form;
};

export const logout = async () => {
  const data = await showModal({
    title: "提示",
    content: "确定注销并退出系统吗？",
  });
  if (data === "ok") {
    setStorage("Authorization", "");
    window.location.href = origin + "#/login";
    window.location.reload();
  }
};

export const downloadFile = (
  str: string,
  filename: string,
  fileType = "xlsx"
) => {
  const url = window.URL.createObjectURL(new Blob([str]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.${fileType}`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

type TreeNodes = {
  label: string;
  id: number;
  children?: TreeNodes[];
};

export const findNodesWithoutChildren = (treeData: TreeNodes[]): number[] => {
  const result: number[] = [];
  const traverse = (nodes: TreeNodes[]) => {
    for (const node of nodes) {
      if (!node.children || node.children.length === 0) {
        result.push(node.id);
      } else {
        traverse(node.children);
      }
    }
  };

  traverse(treeData);
  return result;
};
export const getFirstDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

// 切片上传相关类型定义
interface ChunkInfo {
  chunk: Blob;
  index: number;
  hash: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
}

interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

interface UploadState {
  hash: string;
  uploadedChunks: number[];
  totalChunks: number;
}

// 由于没有后端切片接口，我们使用本地存储来模拟断点续传
const getUploadedChunks = async (hash: string): Promise<number[]> => {
  try {
    const stored = localStorage.getItem(`upload_chunks_${hash}`);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    return [];
  }
};

// 保存已上传的切片信息
const saveUploadedChunks = (hash: string, chunks: number[]) => {
  try {
    localStorage.setItem(`upload_chunks_${hash}`, JSON.stringify(chunks));
  } catch (error) {
    console.error('保存切片信息失败:', error);
  }
};

// 检查文件是否已存在（简化版本）
const checkFileExists = async (hash: string): Promise<string | null> => {
  // 由于没有后端接口，这里返回null，表示文件不存在
  return null;
};

export const customRequest = async (options: any, list: any[], onProgress?: (progress: UploadProgress) => void) => {
  const { file, onSuccess, onError, onStart, onChange } = options;
  
  return new Promise(async (res, rej) => {
    try {
      // 暂时所有文件都使用普通上传
      // 等服务端开发切片合并接口后再启用切片上传
      const { data } = await upload(file, (progressEvent: any) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress?.({
          loaded: progressEvent.loaded,
          total: progressEvent.total,
          percent
        });
      });
      
      onSuccess();
      return res([...list, { url: data, uid: file.uid, name: file.name }]);
    } catch (error) {
      console.error('上传失败:', error);
      onError();
      return rej(error);
    }
  });
};

// 生成文件hash
const generateFileHash = async (file: File): Promise<string> => {
  return new Promise((resolve) => {
    // 使用文件名、大小和时间戳生成简单的hash
    const hash = btoa(`${file.name}_${file.size}_${file.lastModified}`)
      .replace(/[^a-zA-Z0-9]/g, '')
      .substring(0, 16);
    
    resolve(hash);
  });
};

// 文件分片
const createFileChunks = (file: File, chunkSize: number = 2 * 1024 * 1024): Blob[] => {
  const chunks: Blob[] = [];
  let cur = 0;
  while (cur < file.size) {
    chunks.push(file.slice(cur, cur + chunkSize));
    cur += chunkSize;
  }
  return chunks;
};

// 切片上传（使用现有的upload接口）
const uploadWithChunks = async (
  file: File, 
  onProgress?: (progress: UploadProgress) => void
): Promise<string> => {
  const chunkSize = 500 * 1024; // 500KB 每片，进一步避免服务端报错
  const chunks = createFileChunks(file, chunkSize);
  const fileHash = await generateFileHash(file);
  const totalChunks = chunks.length;
  
  // 获取已上传的切片
  const uploadedChunks = await getUploadedChunks(fileHash);
  let totalUploaded = uploadedChunks.length * chunkSize;
  const uploadedUrls: string[] = [];
  
  // 串行上传切片以更好地控制进度
  for (let index = 0; index < chunks.length; index++) {
    // 跳过已上传的切片
    if (uploadedChunks.includes(index)) {
      continue;
    }
    
    const chunk = chunks[index];
    
    try {
      // 为每个切片创建一个新的File对象，包含切片信息
      const chunkFile = new File([chunk], `${file.name}.part${index}`, {
        type: file.type
      });
      
      // 使用现有的upload接口上传切片
      const response = await upload(chunkFile, (progressEvent: any) => {
        const chunkProgress = progressEvent.loaded;
        const currentTotalProgress = totalUploaded + chunkProgress;
        const percent = Math.round((currentTotalProgress * 100) / file.size);
        
        onProgress?.({
          loaded: currentTotalProgress,
          total: file.size,
          percent
        });
      });
      
      // 保存切片URL
      uploadedUrls.push(response.data);
      
      // 保存已上传的切片信息
      uploadedChunks.push(index);
      saveUploadedChunks(fileHash, uploadedChunks);
      
      totalUploaded += chunk.size;
      
      // 更新进度
      const percent = Math.round((totalUploaded * 100) / file.size);
      onProgress?.({
        loaded: totalUploaded,
        total: file.size,
        percent
      });
    } catch (error) {
      throw new Error(`切片 ${index} 上传失败: ${error}`);
    }
  }
  
  // 如果所有切片都上传完成，尝试请求后端合并切片
  if (uploadedUrls.length === totalChunks) {
    try {
      // 这里需要后端提供合并切片的接口
      const mergeResponse = await mergeChunksRequest({
        hash: fileHash,
        fileName: file.name,
        fileSize: file.size,
        chunkSize,
        totalChunks,
        chunkUrls: uploadedUrls
      });
      return mergeResponse.data || uploadedUrls[0]; // 返回合并后的URL或第一个切片URL
    } catch (error) {
      console.warn('合并切片失败，返回第一个切片URL:', error);
      return uploadedUrls[0];
    }
  }
  
  return uploadedUrls.length > 0 ? uploadedUrls[0] : '';
};

// 请求后端合并切片
const mergeChunksRequest = async (params: {
  hash: string;
  fileName: string;
  fileSize: number;
  chunkSize: number;
  totalChunks: number;
  chunkUrls: string[];
}): Promise<any> => {
  // 这里需要后端提供合并切片的接口
  // 如果后端没有这个接口，可以注释掉这个函数调用
  return http.post('/file/mergeChunks', params);
};

export const normFile = (e: any) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};


